/* Public sandbox landing page at /sandbox/:token.
 *
 * iter98.93 — Drops the visitor into a fully-functional Invisique
 * doctor portal experience without a real signup. They provide
 * name + email + phone (we need phone for sales follow-up), we mint
 * a fresh `is_demo=True` doctor account under the hood + seed it
 * with the Demo · Riya Sharma patient + sample case, and redirect
 * them to /doctor.
 *
 * UX intent — make this feel like an exclusive invitation, not a
 * generic signup. Sales reps share this URL with prospects; the
 * landing page leans on the creator's name ("Priya invited you to
 * explore the portal") to humanise the experience.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Sparkle, Cube, CheckCircle, ChatsCircle, ArrowRight, LockKey,
    SmileyXEyes,
} from "@phosphor-icons/react";

const FEATURE_BULLETS = [
    { icon: Cube,         text: "Browse pre-loaded patients + cases" },
    { icon: CheckCircle,  text: "Approve plans or request revisions like a real doctor would" },
    { icon: ChatsCircle,  text: "Try the patient chat, plan viewer, and case wizard end-to-end" },
];

export default function SandboxLanding() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [status, setStatus] = useState("validating"); // validating | ready | expired | claiming
    const [meta, setMeta] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        let alive = true;
        api.get(`/sandbox/${token}`)
            .then((r) => {
                if (!alive) return;
                setMeta(r.data || {});
                setStatus("ready");
            })
            .catch((err) => {
                if (!alive) return;
                setStatus("expired");
                setMeta({ message: err.response?.data?.detail || "This demo link is no longer valid." });
            });
        return () => { alive = false; };
    }, [token]);

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please share your name";
        if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Looks like an incomplete email";
        if (form.phone.trim().length < 6) e.phone = "Phone number too short";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const enter = async () => {
        if (!validate()) return;
        setStatus("claiming");
        try {
            const { data } = await api.post(`/sandbox/${token}/claim`, {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
            });
            // Persist the auth bundle so subsequent navigation has
            // user + token in scope.
            login(data.token, data.user);
            toast.success("Welcome — you're in the sandbox");
            navigate("/doctor", { replace: true });
        } catch (err) {
            setStatus("ready");
            toast.error(err.response?.data?.detail || "Couldn't open the sandbox. Try again or ping the person who shared this link.");
        }
    };

    if (status === "validating") {
        return (
            <div className="min-h-screen grid place-items-center bg-gradient-to-br from-primary/[0.06] via-background to-background">
                <div className="text-center text-muted-foreground text-sm">Validating your demo link…</div>
            </div>
        );
    }

    if (status === "expired") {
        return (
            <div className="min-h-screen grid place-items-center bg-gradient-to-br from-rose-50 via-background to-background px-4">
                <div className="max-w-md w-full text-center bg-card border border-border rounded-3xl p-8 shadow-sm" data-testid="sandbox-expired">
                    <div className="w-14 h-14 rounded-2xl bg-rose-100 grid place-items-center text-rose-600 mx-auto mb-3">
                        <SmileyXEyes size={28} weight="duotone" />
                    </div>
                    <h1 className="font-display text-2xl font-bold">This demo link isn&apos;t available</h1>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        {meta?.message || "It might have expired, been revoked, or the URL was mistyped."} Ask the person who shared it for a fresh link, or sign up for a real Invisique™ account directly.
                    </p>
                    <Button
                        className="mt-5 bg-primary text-primary-foreground"
                        onClick={() => navigate("/doctor-signup")}
                        data-testid="sandbox-expired-signup-btn"
                    >
                        Sign up for Invisique™ <ArrowRight size={13} className="ml-1.5" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen grid lg:grid-cols-5 bg-gradient-to-br from-primary/[0.04] via-background to-background"
            data-testid="sandbox-landing"
        >
            {/* LEFT — pitch */}
            <aside className="lg:col-span-3 px-6 sm:px-12 py-10 lg:py-16 flex flex-col justify-center">
                <div className="max-w-xl">
                    <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-bold uppercase text-primary">
                        <Sparkle size={11} weight="fill" /> You&apos;ve been invited
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3 leading-[1.05] tracking-tight">
                        {meta?.creator_name
                            ? <><span className="text-primary">{meta.creator_name}</span> invited you to explore Invisique™</>
                            : <>Welcome to <span className="text-primary">Invisique™</span></>
                        }
                    </h1>
                    <p className="text-base text-foreground/80 mt-4 leading-relaxed">
                        Step into a fully-functional clear-aligner provider portal — no signup, no commitment. Built for orthodontists and dentists who want a side-by-side view with our planning team.
                    </p>

                    <ul className="mt-6 space-y-2.5">
                        {FEATURE_BULLETS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-foreground/90">
                                    <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                                        <Icon size={14} weight="duotone" />
                                    </span>
                                    {b.text}
                                </li>
                            );
                        })}
                    </ul>

                    <div className="mt-7 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <LockKey size={12} weight="duotone" />
                        Sandbox data only — nothing you do affects real patients or clinics.
                    </div>
                </div>
            </aside>

            {/* RIGHT — claim form */}
            <section className="lg:col-span-2 px-6 sm:px-10 py-10 lg:py-16 flex items-center justify-center">
                <div className="w-full max-w-md bg-card border border-border rounded-3xl p-6 sm:p-7 shadow-sm">
                    <h2 className="font-display text-xl font-bold">Enter the portal</h2>
                    <p className="text-xs text-muted-foreground mt-1">Three quick details and you&apos;re in.</p>

                    <div className="space-y-3 mt-5">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/70 block mb-1">Your name</label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="Dr. Anjali Mehta"
                                data-testid="sandbox-claim-name"
                                aria-invalid={!!errors.name}
                                className={errors.name ? "border-rose-300" : ""}
                            />
                            {errors.name && <p className="text-[10.5px] text-rose-600 mt-1">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/70 block mb-1">Email</label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                placeholder="anjali@clinic.com"
                                data-testid="sandbox-claim-email"
                                aria-invalid={!!errors.email}
                                className={errors.email ? "border-rose-300" : ""}
                            />
                            {errors.email && <p className="text-[10.5px] text-rose-600 mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/70 block mb-1">Phone</label>
                            <Input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                placeholder="+91 98XXXX XXXX"
                                data-testid="sandbox-claim-phone"
                                aria-invalid={!!errors.phone}
                                className={errors.phone ? "border-rose-300" : ""}
                            />
                            {errors.phone && <p className="text-[10.5px] text-rose-600 mt-1">{errors.phone}</p>}
                        </div>
                    </div>

                    <Button
                        onClick={enter}
                        disabled={status === "claiming"}
                        className="w-full mt-5 bg-primary text-primary-foreground"
                        data-testid="sandbox-claim-submit"
                    >
                        {status === "claiming" ? "Opening your sandbox…" : <>Enter the portal <ArrowRight size={13} className="ml-1.5" /></>}
                    </Button>

                    <p className="text-[10.5px] text-muted-foreground mt-4 leading-relaxed">
                        We&apos;ll use your details only to follow up about this demo. You can request deletion anytime. By entering, you agree to the Invisique™ <span className="underline">terms</span> and <span className="underline">privacy policy</span>.
                    </p>
                </div>
            </section>
        </div>
    );
}
